import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DeliveryNoteSelection, OpenDeliveryNote, SelectedItem } from './types';
import { getCustomerOpenDeliveryNotes, getDocumentDetails } from '../../api/client';

interface DocumentLineItem {
  item_id?: number;
  catalog_number?: string;
  description?: string;
  quantity: number;
  price_nis?: number;
  total_nis?: number;
}

interface DeliveryNoteSelectionStepProps {
  customerId: number | null;
  selectedNotes: DeliveryNoteSelection[];
  onChange: (notes: DeliveryNoteSelection[], items: SelectedItem[]) => void;
}

export default function DeliveryNoteSelectionStep({
  customerId,
  selectedNotes,
  onChange,
}: DeliveryNoteSelectionStepProps) {
  const { t } = useTranslation();
  const [notes, setNotes] = useState<OpenDeliveryNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedNotes, setExpandedNotes] = useState<Set<string>>(new Set());
  const [lineItems, setLineItems] = useState<Record<string, DocumentLineItem[]>>({});
  const [lineItemsLoading, setLineItemsLoading] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!customerId) {
      setNotes([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getCustomerOpenDeliveryNotes(customerId)
      .then((res) => {
        if (!cancelled) setNotes(res.data || []);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'Failed to load delivery notes');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [customerId]);

  const selectedSet = useMemo(() => {
    const set = new Set<string>();
    selectedNotes.forEach((note) => set.add(`${note.document_type}#${note.document_number}`));
    return set;
  }, [selectedNotes]);

  const selectedTotal = useMemo(
    () => selectedNotes.reduce((sum, note) => sum + (note.amount || 0), 0),
    [selectedNotes],
  );

  const loadLineItems = useCallback(async (note: OpenDeliveryNote) => {
    const key = `${note.document_type}#${note.document_number}`;
    if (lineItems[key]) return lineItems[key];
    setLineItemsLoading((prev) => new Set(prev).add(key));
    try {
      const res = await getDocumentDetails(note.document_type, note.document_number);
      const items = res.data.items || [];
      setLineItems((prev) => ({ ...prev, [key]: items }));
      return items;
    } catch {
      setLineItems((prev) => ({ ...prev, [key]: [] }));
      return [];
    } finally {
      setLineItemsLoading((prev) => { const next = new Set(prev); next.delete(key); return next; });
    }
  }, [lineItems]);

  const buildItemsFromSelection = useCallback((
    nextNotes: DeliveryNoteSelection[],
    cache: Record<string, DocumentLineItem[]>,
  ): SelectedItem[] => nextNotes.flatMap((note) => {
    const key = `${note.document_type}#${note.document_number}`;
    return (cache[key] || []).map((item, idx) => ({
      id: `delivery-note-${key}-${idx}`,
      item_id: item.item_id,
      catalog_number: item.catalog_number,
      description: item.description || `${t('createDocument.deliveryNotes.deliveryNote')} #${note.document_number}`,
      quantity: item.quantity || 1,
      price: item.price_nis ?? (item.quantity ? (item.total_nis || 0) / item.quantity : 0),
      unit: '',
      is_custom: item.item_id == null,
    }));
  }), [t]);

  const toggleNote = useCallback(async (note: OpenDeliveryNote) => {
    const key = `${note.document_type}#${note.document_number}`;
    if (selectedSet.has(key)) {
      const nextNotes = selectedNotes.filter(
        (selected) => !(selected.document_type === note.document_type && selected.document_number === note.document_number),
      );
      onChange(nextNotes, buildItemsFromSelection(nextNotes, lineItems));
      return;
    }

    const loadedItems = await loadLineItems(note);
    const nextLineItems = { ...lineItems, [key]: loadedItems };
    const nextNotes = [
      ...selectedNotes,
      {
        document_type: note.document_type,
        document_number: note.document_number,
        document_date: note.document_date,
        document_type_name: note.document_type_name,
        amount: note.amount,
      },
    ];
    onChange(nextNotes, buildItemsFromSelection(nextNotes, nextLineItems));
  }, [buildItemsFromSelection, lineItems, loadLineItems, onChange, selectedNotes, selectedSet]);

  const toggleExpand = useCallback(async (note: OpenDeliveryNote) => {
    const key = `${note.document_type}#${note.document_number}`;
    if (expandedNotes.has(key)) {
      setExpandedNotes((prev) => { const next = new Set(prev); next.delete(key); return next; });
      return;
    }
    setExpandedNotes((prev) => new Set(prev).add(key));
    await loadLineItems(note);
  }, [expandedNotes, loadLineItems]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-500">
        <span className="animate-spin mr-2">⏳</span>
        {t('createDocument.deliveryNotes.loading')}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-600 text-sm">
        {error}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-800">
          {t('createDocument.deliveryNotes.title')}
        </h2>
        <p className="text-sm text-gray-500">
          {t('createDocument.deliveryNotes.subtitle')}
        </p>
      </div>

      {notes.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-gray-200 p-8 text-center text-gray-500">
          {t('createDocument.deliveryNotes.noOpenDeliveryNotes')}
        </div>
      ) : (
        <div className="rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-3 py-3 text-start font-medium text-gray-500 w-10"></th>
                <th className="px-3 py-3 text-start font-medium text-gray-500 w-10"></th>
                <th className="px-3 py-3 text-start font-medium text-gray-500">
                  {t('createDocument.deliveryNotes.number')}
                </th>
                <th className="px-3 py-3 text-start font-medium text-gray-500">
                  {t('createDocument.invoices.date')}
                </th>
                <th className="px-3 py-3 text-end font-medium text-gray-500">
                  {t('createDocument.deliveryNotes.amount')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {notes.map((note) => {
                const key = `${note.document_type}#${note.document_number}`;
                const selected = selectedSet.has(key);
                const expanded = expandedNotes.has(key);
                const loadingItems = lineItemsLoading.has(key);
                const items = lineItems[key];
                return (
                  <React.Fragment key={key}>
                    <tr className={`transition-colors ${selected ? 'bg-blue-50/50' : 'hover:bg-gray-50'}`}>
                      <td className="px-2 py-3 text-center">
                        <button
                          type="button"
                          onClick={() => toggleExpand(note)}
                          className="inline-flex items-center justify-center w-5 h-5 rounded hover:bg-gray-200 transition-colors text-gray-400"
                          aria-label={expanded ? 'Collapse' : 'Expand'}
                        >
                          {loadingItems ? (
                            <span className="inline-block w-3 h-3 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
                          ) : (
                            <span className={`inline-block text-xs transition-transform ${expanded ? 'rotate-90' : ''}`}>▶</span>
                          )}
                        </button>
                      </td>
                      <td className="px-3 py-3">
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleNote(note)}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                        />
                      </td>
                      <td className="px-3 py-3 font-medium text-gray-800">
                        {note.document_number}
                      </td>
                      <td className="px-3 py-3 text-gray-600">
                        {note.document_date}
                      </td>
                      <td className="px-3 py-3 text-end font-medium text-gray-800">
                        ₪{Number(note.amount || 0).toFixed(2)}
                      </td>
                    </tr>
                    {expanded && (
                      <tr>
                        <td colSpan={5} className="p-0 bg-gray-50/60">
                          {loadingItems ? (
                            <div className="px-10 py-3 text-xs text-gray-400">
                              {t('documents.loadingItems', 'Loading items...')}
                            </div>
                          ) : items && items.length > 0 ? (
                            <table className="w-full text-xs">
                              <tbody className="divide-y divide-gray-50">
                                {items.map((item, idx) => (
                                  <tr key={idx} className="bg-white/60">
                                    <td className="w-10 px-2 py-1.5" />
                                    <td className="px-4 py-1.5 text-gray-800">{item.description || '—'}</td>
                                    <td className="px-4 py-1.5 text-end text-gray-600">{item.quantity}</td>
                                    <td className="px-4 py-1.5 text-end text-gray-600">
                                      {item.price_nis != null ? `₪${item.price_nis.toFixed(2)}` : '—'}
                                    </td>
                                    <td className="px-4 py-1.5 text-end font-medium text-gray-800">
                                      {item.total_nis != null ? `₪${item.total_nis.toFixed(2)}` : '—'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <div className="px-10 py-2 text-xs text-gray-400">
                              {t('documents.noItems', 'No line items')}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selectedNotes.length > 0 && (
        <div className="flex items-center justify-between rounded-xl border border-blue-200 bg-blue-50 p-4 text-blue-700">
          <span className="text-sm font-semibold">
            {t('createDocument.deliveryNotes.selectedTotal')}: ₪{selectedTotal.toFixed(2)}
          </span>
          <span className="text-xs text-gray-500">
            {selectedNotes.length} {t('createDocument.deliveryNotes.selectedCount')}
          </span>
        </div>
      )}
    </div>
  );
}
